import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Modal,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Fade,
  Chip,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { TABLE_PAPER_SX, HEAD_CELL_SX, TABLE_ROW_SX, BODY_CELL_SX } from "../common/tableStyles";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
  useDeleteCTCrevisions,
  useEmployeeCTCrevisions,
  useGetSingleEmployee,
  useMonthlyCTC,
} from "../../hooks/useEmployees";
import { useAlert } from "../../context/AlertContext";
import { useDeleteConfirmation } from "../../context/DeleteConfirmationContext";

export default function Monthlyctc({ open, handlemodelclose, selectedEmp, monthCTC, CTC }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { showWarning } = useAlert();
  const monthlymutation = useMonthlyCTC();
  const { data } = useGetSingleEmployee({ id: selectedEmp?.employee_no });
  const { data: ctcrevision } = useEmployeeCTCrevisions({ id: data?.['employee']?.id });
  const [showPFConfirm, setShowPFConfirm] = useState(false);
  const [isPFChecked, setIsPFChecked] = useState(false);
  const { showDeleteConfirmation } = useDeleteConfirmation();
  const deleteMutation = useDeleteCTCrevisions();

  const [rows, setRows] = useState([
    {
      fromDate: "",
      amount: "",
      remarks: "",
      modifiedOn: "",
    },
  ]);

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: '95%', sm: '90%', md: 700, lg: 800 },
    maxHeight: { xs: '90vh', md: '85vh' },
    bgcolor: "background.paper",
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    p: { xs: 2, sm: 3, md: 4 },
    overflow: 'auto',
  };

  useEffect(() => {
    if (ctcrevision?.ctc_revisions) {
      setRows(
        ctcrevision.ctc_revisions.map((rev) => ({
          id: rev.id,
          fromDate: rev.from_date,
          amount: rev.amount,
          remarks: rev.remarks,
          modifiedOn: rev.modified_on,
        }))
      );
    }
  }, [ctcrevision]);

  const handleAddRow = () => {
    const newRow = {
      fromDate: "",
      amount: "",
      remarks: "",
      modifiedOn: new Date().toISOString().split("T")[0],
    };
    setRows([...rows, newRow]);
  };

  const handleDeleteRow = (rowToDelete) => {
    const id = rowToDelete?.id;

    if (id) {
      showDeleteConfirmation(id, () => {
        // Handle the actual deletion logic here
        deleteMutation.mutate(id);
        console.log(`${id} has been deleted`);
      });
    } else {
      setRows((prevRows) => prevRows.filter((r) => r !== rowToDelete));
    }
  };

  const handleInputChange = (index, field, value) => {
    const updatedRows = rows.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    setRows(updatedRows);
  };

  const handleSubmit = () => {
    const lastRow = rows.slice(-1)?.[0];
    if (!lastRow?.fromDate || !lastRow?.amount || !lastRow?.remarks) {
      showWarning("Please fill out all fields before submitting.");
      return;
    }
    setShowPFConfirm(true);
  };

  const handleFinalSubmit = () => {
    const lastRow = rows.slice(-1)?.[0];
    const employee_no = selectedEmp.employee_no;
    const monthly_ctc = lastRow.amount;
    const amount = lastRow.amount;
    const from_date = lastRow.fromDate;
    const remarks = lastRow.remarks;
    const pf = isPFChecked;

    monthlymutation.mutate({ employee_no, monthly_ctc, amount, from_date, remarks, pf });
  };

  useEffect(() => {
    if (monthlymutation.isSuccess) {
      setRows([]);
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthlymutation.isSuccess]);

  const handleClose = () => {
    handlemodelclose();
    setShowPFConfirm(false);
    setIsPFChecked(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-title"
      closeAfterTransition
    >
      <Fade in={open}>
        <Box sx={modalStyle}>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '2px solid',
              borderImage: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%) 1',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: { xs: 40, sm: 48 },
                  height: { xs: 40, sm: 48 },
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
                }}
              >
                <AccountBalanceIcon
                  sx={{
                    fontSize: { xs: 24, sm: 28 },
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                />
              </Box>
              <Box>
                <Typography
                  variant={isMobile ? "h6" : "h5"}
                  fontWeight={700}
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Monthly CTC Revisions
                </Typography>
                {selectedEmp && (
                  <Typography variant="body2" color="text.secondary" fontSize={{ xs: '11px', sm: '12px' }}>
                    Employee: {selectedEmp.name || selectedEmp.employee_no}
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              onClick={handleClose}
              sx={{
                background: 'rgba(102, 126, 234, 0.1)',
                '&:hover': {
                  background: 'rgba(102, 126, 234, 0.2)',
                  transform: 'rotate(90deg)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Table Container */}
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              ...TABLE_PAPER_SX,
              mb: 3,
              maxHeight: { xs: '400px', md: '500px' },
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '10px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#667eea',
                borderRadius: '10px',
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={HEAD_CELL_SX}>
                    From Date
                  </TableCell>
                  <TableCell sx={HEAD_CELL_SX}>
                    Amount (₹)
                  </TableCell>
                  <TableCell sx={HEAD_CELL_SX}>
                    Remarks
                  </TableCell>
                  <TableCell sx={HEAD_CELL_SX}>
                    Modified On
                  </TableCell>
                  <TableCell align="center" sx={HEAD_CELL_SX}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={index} sx={TABLE_ROW_SX}>
                    <TableCell sx={{ ...BODY_CELL_SX, padding: { xs: '8px', sm: '12px' } }}>
                      <TextField
                        type="date"
                        value={row.fromDate}
                        onChange={(e) => handleInputChange(index, "fromDate", e.target.value)}
                        size="small"
                        fullWidth
                        InputProps={{
                          sx: {
                            borderRadius: '8px',
                            fontSize: { xs: '11px', sm: '13px' },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, padding: { xs: '8px', sm: '12px' } }}>
                      <TextField
                        type="number"
                        value={row.amount}
                        onChange={(e) => handleInputChange(index, "amount", e.target.value)}
                        placeholder={monthCTC}
                        size="small"
                        fullWidth
                        InputProps={{
                          sx: {
                            borderRadius: '8px',
                            fontSize: { xs: '11px', sm: '13px' },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, padding: { xs: '8px', sm: '12px' } }}>
                      <TextField
                        type="text"
                        value={row.remarks}
                        onChange={(e) => handleInputChange(index, "remarks", e.target.value)}
                        size="small"
                        fullWidth
                        placeholder="Enter remarks..."
                        InputProps={{
                          sx: {
                            borderRadius: '8px',
                            fontSize: { xs: '11px', sm: '13px' },
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...BODY_CELL_SX, padding: { xs: '8px', sm: '12px' } }}>
                      <Chip
                        label={row.modifiedOn || 'N/A'}
                        size="small"
                        sx={{
                          background: 'rgba(102, 126, 234, 0.1)',
                          color: '#667eea',
                          fontWeight: 600,
                          fontSize: { xs: '10px', sm: '11px' },
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ ...BODY_CELL_SX, padding: { xs: '8px', sm: '12px' } }}>
                      <IconButton
                        onClick={() => handleDeleteRow(row)}
                        size="small"
                        sx={{
                          color: '#dc3545',
                          '&:hover': {
                            background: 'rgba(220, 53, 69, 0.1)',
                            transform: 'scale(1.1)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexWrap: 'wrap',
              mb: showPFConfirm ? 3 : 0,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={handleAddRow}
              sx={{
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                borderRadius: '12px',
                padding: { xs: '10px 20px', sm: '12px 24px' },
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                boxShadow: '0 8px 20px rgba(79, 172, 254, 0.4)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '13px', sm: '14px' },
                '&:hover': {
                  boxShadow: '0 12px 28px rgba(79, 172, 254, 0.5)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Add Row
            </Button>
            <Button
              variant="contained"
              startIcon={<CheckCircleIcon />}
              onClick={handleSubmit}
              sx={{
                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                borderRadius: '12px',
                padding: { xs: '10px 20px', sm: '12px 24px' },
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                boxShadow: '0 8px 20px rgba(67, 233, 123, 0.4)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: { xs: '13px', sm: '14px' },
                '&:hover': {
                  boxShadow: '0 12px 28px rgba(67, 233, 123, 0.5)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Submit
            </Button>
          </Box>

          {/* PF Confirmation Box */}
          {showPFConfirm && (
            <Fade in={showPFConfirm}>
              <Paper
                elevation={0}
                sx={{
                  padding: { xs: 2, sm: 3 },
                  borderRadius: '16px',
                  border: '2px solid #667eea',
                  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                  sx={{
                    fontSize: { xs: '14px', sm: '16px' },
                    color: '#212529',
                  }}
                >
                  PF Configuration
                </Typography>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isPFChecked}
                      onChange={(e) => setIsPFChecked(e.target.checked)}
                      sx={{
                        '&.Mui-checked': {
                          color: '#667eea',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                      Apply PF for this employee
                    </Typography>
                  }
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    onClick={handleFinalSubmit}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1' },
                      borderRadius: '12px',
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      boxShadow: '0 8px 20px rgba(102, 126, 234, 0.4)',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xs: '13px', sm: '14px' },
                      '&:hover': {
                        boxShadow: '0 12px 28px rgba(102, 126, 234, 0.5)',
                      },
                    }}
                  >
                    Confirm & Submit
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setShowPFConfirm(false)}
                    sx={{
                      flex: { xs: '1 1 100%', sm: '1' },
                      borderRadius: '12px',
                      padding: '10px 24px',
                      borderColor: '#dc3545',
                      color: '#dc3545',
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: { xs: '13px', sm: '14px' },
                      '&:hover': {
                        borderColor: '#dc3545',
                        background: 'rgba(220, 53, 69, 0.05)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              </Paper>
            </Fade>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}